import {
  hasLevel,
  ladderAccess,
  useAccess,
  useCampuses,
  useReportBuilder,
  useTeam,
} from '@church/data'
import { METRICS, type ReportDefinition } from '@church/domain'
import {
  faChevronRight,
  faDownload,
  faEllipsis,
  faFloppyDisk,
  faSliders,
  faTrashCan,
} from '@church/ui-icons'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { useState } from 'react'
import { apiBaseUrl } from '../lib/api.ts'
import {
  Button,
  Card,
  ConfirmButton,
  EmptyState,
  ErrorNote,
  Field,
  Icon,
  IconButton,
  Input,
  listRow,
  ListTitle,
  PageHeader,
  PanelSection,
  Select,
  SidePanel,
} from '../components/ui.tsx'

/** Reports: choose numbers, a period and a grain; download, save or schedule. */
export const Route = createFileRoute('/_authed/_shell/reports')({
  beforeLoad: ({ context }) => {
    if (!hasLevel(ladderAccess(context.me), 'reports', 'viewer')) throw redirect({ to: '/' })
  },
  component: ReportsScreen,
})

const RANGES: { label: string; range: ReportDefinition['range'] }[] = [
  { label: 'Last 4 weeks', range: { kind: 'last', count: 4, unit: 'weeks' } },
  { label: 'Last 12 weeks', range: { kind: 'last', count: 12, unit: 'weeks' } },
  { label: 'Last 6 months', range: { kind: 'last', count: 6, unit: 'months' } },
  { label: 'Last 12 months', range: { kind: 'last', count: 12, unit: 'months' } },
]

function ReportsScreen() {
  const b = useReportBuilder()
  const campuses = useCampuses()
  const team = useTeam()
  const access = useAccess()
  const [name, setName] = useState('')
  const [schedule, setSchedule] = useState<'' | 'weekly' | 'monthly'>('')
  const [format, setFormat] = useState<'csv' | 'xlsx' | 'pdf'>('xlsx')
  const [recipients, setRecipients] = useState<string[]>([])
  // Below xl the options sit behind a button, so the numbers come first.
  const [choosing, setChoosing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [openId, setOpenId] = useState<string | null>(null)
  const [shownId, setShownId] = useState<string | null>(null)
  const shownReport = b.saved.find((report) => report.id === shownId)
  const rangeIndex = RANGES.findIndex(
    (r) => JSON.stringify(r.range) === JSON.stringify(b.definition.range),
  )

  return (
    <div className="space-y-6">
      <PageHeader
        title="Reports"
        action={
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              icon={faSliders}
              className="xl:hidden"
              aria-expanded={choosing}
              onClick={() => setChoosing((v) => !v)}
            >
              Options
            </Button>
            {access.has('reports', 'editor') && (
              <Button icon={faFloppyDisk} onClick={() => setSaving(true)}>
                Save
              </Button>
            )}
          </div>
        }
      />
      <ErrorNote error={b.error} />
      <div className="grid gap-6 xl:grid-cols-4">
        <Card className={`h-fit space-y-4 p-4 xl:block ${choosing ? '' : 'hidden'}`}>
          <fieldset className="space-y-2">
            <legend className="text-sm font-semibold text-content-default">Numbers</legend>
            {b.allowed.map((metric) => (
              <label key={metric} className="flex items-center gap-2 text-sm text-content-default">
                <input
                  type="checkbox"
                  className="size-4 accent-brand-solid"
                  checked={b.definition.metrics.includes(metric)}
                  onChange={() => b.toggleMetric(metric)}
                />
                {METRICS[metric].label}
              </label>
            ))}
          </fieldset>
          <Field label="Period">
            <Select
              value={String(rangeIndex)}
              onChange={(event) => {
                const choice = RANGES[Number(event.target.value)]
                if (choice) b.setRange(choice.range)
              }}
            >
              {RANGES.map((r, i) => (
                <option key={r.label} value={i}>
                  {r.label}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="By">
            <Select
              value={b.definition.interval}
              onChange={(event) =>
                b.setInterval(event.target.value as ReportDefinition['interval'])
              }
            >
              <option value="day">Day</option>
              <option value="week">Week</option>
              <option value="month">Month</option>
            </Select>
          </Field>
          {(campuses.data?.length ?? 0) > 1 && (
            <Field label="Campus">
              <Select
                value={b.definition.campusId ?? ''}
                onChange={(event) => b.setCampus(event.target.value || null)}
              >
                <option value="">Whole church</option>
                {campuses.data?.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            </Field>
          )}
        </Card>

        <div className="min-w-0 space-y-4 xl:col-span-3">
          <Card bleed>
            <ListTitle
              title={`${b.result ? `${b.result.from} to ${b.result.to}` : 'Results'}${
                b.isRunning ? ' · updating…' : ''
              }`}
            >
              <span className="flex gap-1">
                {(['csv', 'xlsx', 'pdf'] as const).map((f) => (
                  <a
                    key={f}
                    href={`${apiBaseUrl}${b.downloadPath(f, name || 'Report')}`}
                    className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium text-content-brand hover:bg-surface-sunken"
                  >
                    <Icon icon={faDownload} className="size-3" />
                    {f.toUpperCase()}
                  </a>
                ))}
              </span>
            </ListTitle>
            {!b.result || b.definition.metrics.length === 0 ? (
              <EmptyState title="Choose at least one number" />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-surface-heading text-left text-xs uppercase tracking-wide text-content-muted">
                    <tr className="border-b border-border-subtle">
                      {b.result.grid[0]?.map((h, i) => (
                        <th
                          key={h}
                          className={`px-4 py-2 font-medium ${i > 0 ? 'text-right' : ''}`}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border-subtle">
                    {b.result.grid.slice(1).map((row, r, rows) => (
                      <tr key={row[0]} className={r === rows.length - 1 ? 'font-semibold' : ''}>
                        {row.map((cell, i) => (
                          <td
                            key={i}
                            className={`px-4 py-2 ${i > 0 ? 'text-right tabular-nums' : 'whitespace-nowrap'} ${r === rows.length - 1 ? 'text-content-default' : 'text-content-muted'}`}
                          >
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          {access.has('reports', 'editor') && (
            <SidePanel
              open={saving}
              onOpenChange={(next) => {
                if (!next) setSaving(false)
              }}
              title="Save this report"
              primary={
                <Button
                  icon={faFloppyDisk}
                  className="py-1.5"
                  pending={b.save.isPending}
                  disabled={!name.trim() || (Boolean(schedule) && recipients.length === 0)}
                  onClick={() =>
                    b.save.mutate(
                      { name, schedule: schedule || null, format, recipients },
                      { onSuccess: () => setSaving(false) },
                    )
                  }
                >
                  Save
                </Button>
              }
            >
              <div className="space-y-3">
                <Field label="Name">
                  <Input
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    placeholder="Sunday numbers"
                  />
                </Field>
                <Field label="Email it">
                  <Select
                    value={schedule}
                    onChange={(event) => setSchedule(event.target.value as typeof schedule)}
                  >
                    <option value="">Don’t send</option>
                    <option value="weekly">Every Monday</option>
                    <option value="monthly">On the 1st</option>
                  </Select>
                </Field>
                <Field label="As">
                  <Select
                    value={format}
                    onChange={(event) => setFormat(event.target.value as typeof format)}
                  >
                    <option value="xlsx">Excel</option>
                    <option value="csv">CSV</option>
                    <option value="pdf">PDF</option>
                  </Select>
                </Field>
                {schedule && (
                  <Field label="To">
                    <Select
                      multiple
                      value={recipients}
                      onChange={(event) =>
                        setRecipients([...event.target.selectedOptions].map((o) => o.value))
                      }
                      className="h-24"
                    >
                      {(team.data ?? []).map((m) => (
                        <option key={m.userId} value={m.userId}>
                          {m.name}
                        </option>
                      ))}
                    </Select>
                  </Field>
                )}
              </div>
              <p className="text-xs text-content-muted">
                Each recipient is checked when it is sent: anyone who can no longer see a number in
                it gets nothing.
              </p>
            </SidePanel>
          )}

          <Card bleed className="divide-y divide-border-subtle">
            <ListTitle title="Saved reports" />
            {b.saved.length === 0 && <EmptyState title="None yet" />}
            {b.saved.map((report) => (
              <div key={report.id} className="flex items-center gap-1 pr-2">
                <button
                  type="button"
                  className={`${listRow} flex min-w-0 flex-1 items-center gap-3 text-left hover:bg-surface-sunken`}
                  onClick={() => b.setDefinition(report.definition)}
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-base font-medium text-content-default">
                      {report.name}
                    </span>
                    <span className="block truncate text-xs text-content-muted">
                      {report.definition.metrics.map((m) => METRICS[m].label).join(', ')}
                    </span>
                  </span>
                  <Icon icon={faChevronRight} className="size-3 text-content-subtle" />
                </button>
                <IconButton
                  icon={faEllipsis}
                  label={`${report.name}: details`}
                  onClick={() => {
                    setShownId(report.id)
                    setOpenId(report.id)
                  }}
                />
              </div>
            ))}
          </Card>
          <SidePanel
            open={openId !== null && shownReport !== undefined}
            onOpenChange={(next) => {
              if (!next) setOpenId(null)
            }}
            title={shownReport?.name ?? ''}
            footer={
              shownReport && access.has('reports', 'editor') ? (
                <ConfirmButton
                  label="Delete report"
                  icon={faTrashCan}
                  variant="quietDanger"
                  className="-ml-4"
                  title={`Delete “${shownReport.name}”?`}
                  confirmLabel="Delete"
                  onConfirm={() =>
                    b.remove.mutate(shownReport.id, { onSuccess: () => setOpenId(null) })
                  }
                />
              ) : undefined
            }
          >
            {shownReport && (
              <>
                <PanelSection title="Numbers">
                  <p className="text-sm text-content-default">
                    {shownReport.definition.metrics.map((m) => METRICS[m].label).join(', ')}
                  </p>
                </PanelSection>
                <PanelSection title="Emailed">
                  <p className="text-sm text-content-default">
                    {shownReport.schedule
                      ? `${shownReport.schedule === 'weekly' ? 'Every Monday' : 'On the 1st'} as ${shownReport.format.toUpperCase()} to ${shownReport.recipients.length} ${shownReport.recipients.length === 1 ? 'person' : 'people'}`
                      : 'Not sent'}
                  </p>
                </PanelSection>
                <Button
                  variant="secondary"
                  className="w-full"
                  onClick={() => {
                    b.setDefinition(shownReport.definition)
                    setOpenId(null)
                  }}
                >
                  Show it
                </Button>
              </>
            )}
          </SidePanel>
        </div>
      </div>
    </div>
  )
}
